import classNames from 'classnames';
import { observer } from 'mobx-react';

import Icon from '../../../app/shared/components/Icon/Icon';
import InputSearch from '../../../app/shared/components/InputSearch/InputSearch';
import ResultsCounter from '../../../app/shared/components/ResultsCounter/ResultsCounter';
import AddProductsToPricelistModal from '../../shared/AddProductsToPricelistModal/AddProductsToPricelistModal';
import ExportPricelistToPdfModal from '../../shared/ExportPricelistToPdfModal/ExportPricelistToPdfModal';
import Button from '../../ui/Button/Button';
import ExpansionToggle from './ExpansionToggle/ExpansionToggle';
import ProductFamilyDetails from './ProductFamilyDetails/ProductFamilyDetails';
import { ProductFamilyDetailsState } from './ProductFamilyDetails/ProductFamilyDetailsState';
import css from './ProductListing.css';
import ProductListingFilters from './ProductListingFilters/ProductListingFilters';
import ProductListingState from './ProductListingState';
import ProductModelDetails from './ProductModelDetails/ProductModelDetails';
import ProductModelDetailsState from './ProductModelDetails/ProductModelDetailsState';
import ProductSelectionPanel from './ProductSelectionPanel/ProductSelectionPanel';
import ProductTable from './ProductTable/ProductTable';

interface ProductListingProps {
  state: ProductListingState;
}

const ProductListing = observer(({ state }: ProductListingProps) => {
  const { translation } = state;

  const isDetailsOpen = state.details !== null;

  return (
    <div className={classNames(css.root, { [css.withDetails]: isDetailsOpen })}>
      <ProductListingFilters state={state.filters} hidden={isDetailsOpen} />
      <section className={css.results} aria-label={translation.title}>
        <div className={css.toolbar}>
          <search className={css.search}>
            <InputSearch
              defaultValue={state.searchTerm}
              placeholderTranslation={translation.searchPlaceholder}
              onValueChange={state.setSearchTerm}
            />
          </search>
          <ExpansionToggle
            className={css.expansionToggle}
            expansion={state.expansion}
            translation={translation.expansionToggle}
          />
          <ResultsCounter translation={translation.productsFoundLabel} value={state.productsCount} />
        </div>
        <div className={css.tableWrapper} aria-busy={state.loadingState === 'loading'}>
          <ProductTable
            families={state.families}
            expansion={state.expansion}
            canLoadMoreProducts={state.canLoadMoreProducts}
            loadingState={state.loadingState}
            selection={state.selection}
            sort={state.sort}
            currency={state.currency}
            translation={translation.table}
            getDetailsRouteUrl={state.getDetailsRouteUrl}
            isItemActive={(id) => id === state.activeItemId}
            onDetailsToggle={(params) => state.toggleDetails(params)}
            onLoadMore={state.loadMoreProducts}
          />
        </div>
        <ProductSelectionPanel
          className={css.selectionPanel}
          selection={state.selection}
          translation={translation.selectionPanel}
        >
          <AddProductsToPricelistModal
            state={state.addToPricelistModal}
            trigger={
              <Button theme="secondary" variant="outline">
                <Icon name="folderPlus" /> {translation.addToPricelistModal.triggerLabel}
              </Button>
            }
          />
          <ExportPricelistToPdfModal
            state={state.exportToPdfModal}
            translation={translation.exportToPdfModal}
            trigger={
              <Button theme="secondary" variant="outline">
                <Icon name="downloadLight" /> {translation.selectionPanel.export}
              </Button>
            }
          />
        </ProductSelectionPanel>
      </section>
      {state.details instanceof ProductFamilyDetailsState && (
        <ProductFamilyDetails
          state={state.details}
          translation={state.translation.details}
          onClose={state.closeDetails}
        />
      )}
      {state.details instanceof ProductModelDetailsState && (
        <ProductModelDetails
          state={state.details}
          translation={state.translation.modelDetails}
          onClose={state.closeDetails}
        />
      )}
    </div>
  );
});

export default ProductListing;
